import React, { useState, useCallback, useContext } from "react";
import { useLocation } from "react-router-dom";
import { useDiaryContext } from "../../../../../hooks/useDiaryContext";
import { AppContext } from "../../../../../context/AppContext";
import Badge from "../../../../Shared/Badge/Badge";
import { Tab } from "../../../../Shared/Tabs/Tab/Tab";
import { TabIcon } from "../../../../Shared/Tabs/Tab/TabIcon";
import { TabLabel } from "../../../../Shared/Tabs/Tab/TabLabel/TabLabel";
import { Tabs } from "../../../../Shared/Tabs/Tabs";
import SelectedBookingGuests from "../Guests/SelectedBookingGuests";
import SelectedBookingHistory from "../History/SelectedBookingHistory";
import SelectedBookingLinked from "../SelectedBookingLinked/SelectedBookingLinked";
import SelectedBookingManage from "../Manage/SelectedBookingManage";
import SelectedBookingMessages from "../Messages/SelectedBookingMessages";
import SelectedBookingDocuments from "../Documents/SelectedBookingDocuments";
import SelectedBookingNotes from "../Notes/SelectedBookingNotes";
import SelectedBookingPayments from "../Payments/SelectedBookingPayments";
import SelectedBookingPreOrders from "../PreOrders/SelectedBookingPreOrders";
import { useBookingPermissions } from "../../../../../hooks/useBookingPermissions";
import {
  getTabList,
  SelectedBookingTabEnum
} from "./SelectedBookingTabsController";

type InitialTabLocationState = { initialBookingTab?: SelectedBookingTabEnum };

const SelectedBookingTabs = () => {
  const location = useLocation();
  const locationState = location.state as InitialTabLocationState | null;
  const [selectedTabKey, setSelectedTabKey] = useState<SelectedBookingTabEnum>(
    locationState?.initialBookingTab ?? SelectedBookingTabEnum.Manage
  );
  const [initialPaymentAmount, setInitialPaymentAmount] = useState<
    number | undefined
  >(undefined);

  const navigateToPaymentsWithAmount = useCallback((amount: number) => {
    setInitialPaymentAmount(amount);
    setSelectedTabKey(SelectedBookingTabEnum.Payments);
  }, []);
  const { selectedBookingV4, selectedBooking } = useDiaryContext();
  const { venueGroupConfig } = useContext(AppContext);
  // Payments tab is enabled when booking is managed by venue and not a walk-in
  const isPaymentsEnabled =
    selectedBookingV4?.managed_by_venue && !selectedBookingV4?.walk_in;

  // PreOrders tab visibility is based on venue group configuration
  const isPreOrdersVenueEnabled = venueGroupConfig?.preorders_enabled ?? true;

  const { userCanReadDocuments } = useBookingPermissions();

  const tabList = getTabList(
    selectedBookingV4,
    selectedBooking,
    isPaymentsEnabled,
    isPreOrdersVenueEnabled,
    userCanReadDocuments
  );

  return (
    <div className="SelectedBookingTabs flex flex-col flex-[1_1_100%] min-h-0 h-full">
      <div className="TabList mx-panel-padding flex">
        <Tabs direction="horizontal" type="secondary">
          {tabList.map((section) =>
            section.isEnabled ? (
              <Tab
                size={section.size || "lg"}
                key={section.title}
                active={section.key === selectedTabKey}
                onClick={() => setSelectedTabKey(section.key)}
              >
                {section.badgeCount > 0 && <Badge count={section.badgeCount} />}
                <>
                  <TabIcon name={section.icon} />
                  <TabLabel>
                    {section.title}
                    {section.count ? ` (${section.count})` : ""}
                  </TabLabel>
                </>
              </Tab>
            ) : (
              ""
            )
          )}
        </Tabs>
      </div>
      <div className="TabContentWrapper min-h-0 min-w-0 bg-page-background ml-[var(--panelBorderWidth)] overflow-hidden flex flex-col grow">
        <div className="TabContent min-h-0 min-w-0 h-0 grow overflow-auto">
          {selectedTabKey === SelectedBookingTabEnum.Manage && (
            <SelectedBookingManage key={selectedBooking?.id} />
          )}
          {selectedTabKey === SelectedBookingTabEnum.Guests && (
            <SelectedBookingGuests />
          )}
          {selectedTabKey === SelectedBookingTabEnum.PreOrders && (
            <SelectedBookingPreOrders
              navigateToPaymentsWithAmount={navigateToPaymentsWithAmount}
            />
          )}
          {selectedTabKey === SelectedBookingTabEnum.Payments && (
            <SelectedBookingPayments
              initialPaymentAmount={initialPaymentAmount}
              onInitialAmountUsed={() => setInitialPaymentAmount(undefined)}
            />
          )}
          {selectedTabKey === SelectedBookingTabEnum.Notes && (
            <SelectedBookingNotes />
          )}
          {selectedTabKey === SelectedBookingTabEnum.Documents && (
            <SelectedBookingDocuments />
          )}
          {selectedTabKey === SelectedBookingTabEnum.Messages && (
            <SelectedBookingMessages />
          )}
          {selectedTabKey === SelectedBookingTabEnum.Linked && (
            <SelectedBookingLinked setSelectedTabKey={setSelectedTabKey} />
          )}
          {selectedTabKey === SelectedBookingTabEnum.History && (
            <SelectedBookingHistory />
          )}
        </div>
      </div>
    </div>
  );
};

export default SelectedBookingTabs;
